import core from "@/core/index";

class CoreUtils {
  format = new FormatUtils();
  validate = new ValidateUtils();
  image = new ImageUtils();

  resetObject(obj) {
    // Handle Array
    if (obj instanceof Array) {
      for (let i = 0, len = obj.length; i < len; i++) {
        const value = obj[i];
        if (value instanceof Object || value instanceof Array) {
          this.resetObject(value);
        } else if (value instanceof String) {
          obj[i] = "";
        } else {
          obj[i] = null;
        }
      }
      return;
    }

    // Handle Object
    else if (obj instanceof Object) {
      for (let attr in obj) {
        if (obj.hasOwnProperty(attr)) {
          const value = obj[attr];
          if (value instanceof Object || value instanceof Array) {
            this.resetObject(value);
          } else if (value instanceof String) {
            obj[attr] = "";
          } else {
            obj[attr] = null;
          }
        }
      }
      return;
    }

    throw new Error("Unable to copy obj! Its type isn't supported.");
  }

  deepCopy(src) {
    // Handle the 3 simple types, and null or undefined
    if (null == src || "object" != typeof src) return src;

    // Handle Date
    if (src instanceof Date) {
      const copy = new Date();
      copy.setTime(src.getTime());
      return copy;
    }

    // Handle Array
    if (src instanceof Array) {
      const copy = [];
      for (let i = 0, len = src.length; i < len; i++) {
        copy[i] = this.deepCopy(src[i]);
      }
      return copy;
    }

    // Handle Object
    if (src instanceof Object) {
      const copy = {};
      for (let attr in src) {
        if (src.hasOwnProperty(attr)) {
          copy[attr] = this.deepCopy(src[attr]);
        }
      }
      return copy;
    }

    throw new Error("Unable to copy obj! Its type isn't supported.");
  }

  copy(src, dest) {
    for (const key of Object.keys(src)) {
      const value = src[key];
      if (typeof value === "object") {
        if (dest[key] == null) {
          dest[key] = this.deepCopy(src[key]);
        } else {
          this.copy(value, dest[key]);
        }
      } else {
        dest[key] = value;
      }
    }
  }

  platform() {
    const userAgent = navigator.userAgent.toLowerCase();
    let platform = "";
    if (userAgent.indexOf("android") > -1) {
      platform = "android";
    } else if (
      userAgent.indexOf("iphone") > -1 ||
      userAgent.indexOf("ipad") > -1 ||
      userAgent.indexOf("ipod") > -1
    ) {
      // IOS
      platform = "ios";
    } else if (userAgent.indexOf("windows") > -1) {
      platform = "windows";
    } else {
      // 윈도우, 아이폰, 안드로이드 외
      platform = "other";
    }
    return platform;
  }

  redirectWebviewBrowser(router) {
    const userAgent = navigator.userAgent.toLowerCase();
    let webView = false;
    let isSccWebView = false;
    let platform = "";

    if (userAgent.indexOf("android") > -1) {
      platform = "android";
      if (userAgent.indexOf("wv") > -1) {
        webView = true;
      }
      if (userAgent.indexOf("scc") > -1) {
        isSccWebView = true;
      }
    } else if (
      userAgent.indexOf("iphone") > -1 ||
      userAgent.indexOf("ipad") > -1 ||
      userAgent.indexOf("ipod") > -1
    ) {
      // IOS
      platform = "ios";
      webView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent);
    } else if (userAgent.indexOf("windows") > -1) {
      platform = "windows";
    } else {
      // 윈도우, 아이폰, 안드로이드 외
      platform = "other";
    }

    if (webView && !isSccWebView) {
      if (platform === "android") {
        const host = this.format.reformatHost(location.href.substring(location.protocol.length));
        const scheme = location.protocol.substr(0, location.protocol.length - 1);
        router.replace("/error/not-support-browser", () => {
          location.href = `intent:${host}#Intent;package=com.android.chrome;scheme=${scheme};end`;
        });
        return true;
      }
    }
    return false;
  }

  loadScript(url) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      let isLoadScript = false;
      let onLoadScript = function () {
        if (isLoadScript) return;
        isLoadScript = true;
        resolve();
      };
      script.onreadystatechange = function () {
        if (this.readyState === "complete" || this.readyState === "loaded") onLoadScript();
      };
      script.onload = onLoadScript;
      const head = document.querySelector("head");
      head.appendChild(script);
    });
  }

  pascalCase(str) {
    return str.replace(/(\w)(\w*)/g, function (g0, g1, g2) {
      return g1.toUpperCase() + g2.toLowerCase();
    });
  }

  windDirectionStandardList = [
    { angle: 11.25, en: "N", ko: "북" },
    { angle: 33.75, en: "NNE", ko: "북북동" },
    { angle: 56.25, en: "NE", ko: "북동" },
    { angle: 78.75, en: "ENE", ko: "동북동" },
    { angle: 101.25, en: "E", ko: "동" },
    { angle: 123.75, en: "ESE", ko: "동남동" },
    { angle: 146.25, en: "SE", ko: "남동" },
    { angle: 168.75, en: "SSE", ko: "남남동" },
    { angle: 191.25, en: "S", ko: "남" },
    { angle: 213.75, en: "SSW", ko: "남남서" },
    { angle: 236.25, en: "SW", ko: "남서" },
    { angle: 258.75, en: "WSW", ko: "서남서" },
    { angle: 281.25, en: "W", ko: "서" },
    { angle: 303.75, en: "WNW", ko: "서북서" },
    { angle: 326.25, en: "NW", ko: "북서" },
    { angle: 348.75, en: "NNW", ko: "북북서" },
    { angle: 360.1, en: "N", ko: "북" },
  ];

  windDirection(value) {
    if (!this.format.onlyNumber(value)) {
      return null;
    }
    value = Number(value);
    const result = {
      angle: value,
      en: "",
      ko: "",
      text: "",
    };

    for (let i = 0; i < this.windDirectionStandardList.length; i++) {
      const standard = this.windDirectionStandardList[i];
      if (value < standard.angle) {
        result.en = standard.en;
        result.ko = standard.ko;
        break;
      }
    }
    result.text = result.ko;
    return result;
  }
}

class ValidateUtils {
  phone(phoneNumber) {
    return /^01(?:0|1|[6-9])[.-]?(\d{3}|\d{4})[.-]?(\d{4})$/.test(phoneNumber);
  }

  isBlank(value) {
    if (value != null) {
      if (typeof value === "object") {
        return false;
      } else {
        return String(value).trim().length === 0;
      }
    }
    return true;
  }

  isNotBlank(value) {
    return !this.isBlank(value);
  }
}

class ImageUtils {
  createSvgElement(tag, attrs) {
    const el = document.createElementNS("http://www.w3.org/2000/svg", tag);
    for (let key in attrs) {
      el.setAttribute(key, attrs[key]);
    }
    return el;
  }

  dataURItoBlob(dataURI) {
    const bytes =
      dataURI.split(",")[0].indexOf("base64") >= 0
        ? atob(dataURI.split(",")[1])
        : unescape(dataURI.split(",")[1]);
    const mime = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const max = bytes.length;
    const ia = new Uint8Array(max);
    for (let i = 0; i < max; i++) ia[i] = bytes.charCodeAt(i);
    return new Blob([ia], { type: mime });
  }

  getImageBlob(file, maxSize) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = function (event) {
        const dataUri = event.target.result;
        const image = new Image();
        image.src = dataUri;
        image.onload = function () {
          let width = image.width;
          let height = image.height;
          if (maxSize != null) {
            if (width > height && width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            } else if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          canvas.getContext("2d").drawImage(image, 0, 0, width, height);
          const dataUri = canvas.toDataURL(file.type);
          resolve({
            filename: file.name,
            blob: core.utils.image.dataURItoBlob(dataUri),
            dataUri: dataUri,
            width: width,
            height: height,
          });
        };
      };
      reader.readAsDataURL(file);
    });
  }

  getImageBlobFromDataURI(filename, dataURI, maxSize) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = dataURI;
      image.onload = function () {
        let width = image.width;
        let height = image.height;
        if (maxSize != null) {
          if (width > height && width > maxSize) {
            height *= maxSize / width;
            width = maxSize;
          } else if (height > maxSize) {
            width *= maxSize / height;
            height = maxSize;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(image, 0, 0, width, height);
        const mime = dataURI.split(",")[0].split(":")[1].split(";")[0];
        dataURI = canvas.toDataURL(mime);
        const blob = core.utils.image.dataURItoBlob(dataURI);
        resolve({
          filename: filename,
          blob: blob,
          dataUri: dataURI,
          width: width,
          height: height,
        });
      };
    });
  }
}

class FormatUtils {
  filesize(si, fileSize, decimalPlaces) {
    const unit = si ? 1000 : 1024;
    if (fileSize < unit) return fileSize + " B";
    const exp = parseInt(String(Math.log(fileSize) / Math.log(unit)));
    const pre = (si ? "kMGTPE" : "KMGTPE").charAt(exp - 1) + (si ? "" : "i");
    if (decimalPlaces == null || decimalPlaces < 0) {
      decimalPlaces = 0;
    }
    return (fileSize / Math.pow(unit, exp)).toFixed(decimalPlaces) + " " + pre + "B";
  }

  money(str) {
    if (str == null) return "";
    return this.onlyNumber(str)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  hyphenPhone(strParam) {
    if (strParam == null) return "";
    const str = strParam.toString().replace(/[^0-9]/g, "");
    let tmp = "";
    const isServiceNumber = str.startsWith("1");
    if (isServiceNumber) {
      const startLength = 4;
      if (str.length < 4) {
        return str;
      } else {
        tmp += str.substr(0, startLength);
        tmp += "-";
        tmp += str.substr(startLength, startLength);
        //console.log("tmp : ", tmp);
        return tmp;
      }
    } else {
      const isSeoul = str.startsWith("02");
      const startLength = isSeoul ? 2 : 3;
      if (str.length < 4) {
        if (isSeoul && str.length === 3) {
          tmp += str.substr(0, startLength);
          tmp += "-";
          tmp += str.substr(startLength);
          return tmp;
        }
        return str;
      } else if (str.length < 7) {
        tmp += str.substr(0, startLength);
        tmp += "-";
        tmp += str.substr(startLength);
        return tmp;
      } else if (str.length < startLength + 8) {
        tmp += str.substr(0, startLength);
        tmp += "-";
        tmp += str.substr(startLength, 3);
        tmp += "-";
        tmp += str.substr(startLength + 3);
        return tmp;
      } else {
        tmp += str.substr(0, startLength);
        tmp += "-";
        tmp += str.substr(startLength, 4);
        tmp += "-";
        tmp += str.substr(startLength + 4, 4);
        return tmp;
      }
    }
  }

  reformatKorMoney(str) {
    return this.onlyNumber(str) + "만원";
  }

  onlyNumber(str) {
    if (str == null) return "";
    return str.toString().replace(/[^0-9]/g, "");
  }

  reformatHost(str) {
    return str.replace(/([^:]\/)\/+/g, "$1");
  }
}

export default new CoreUtils();
